import {
    format,
    formatDistance,
    formatISO,
    isToday,
    isValid,
    parse,
    parseISO,
    startOfDay,
    subDays,
} from 'date-fns';

export default function formatDate(dateString: string): string {
    if (!dateString) return '-';
    return format(new Date(dateString), 'MMM d, yyyy');
}

export function formatUTCDate(
    dateString: string | Date,
    dateFormat = 'MMM d, yyyy',
    preventConversionToLocalTimezone = false,
): string {
    if (!dateString) return '-';
    if (dateString instanceof Date) {
        dateString = formatDateForDB(dateString);
    }
    return format(
        getUTCDate(dateString, preventConversionToLocalTimezone),
        dateFormat,
    );
}

export function formatDateTime(
    dateString: string,
    preventConversionToLocalTimezone = true,
): string {
    return format(
        new Date(
            preventConversionToLocalTimezone
                ? dateString.split('+')[0]
                : dateString,
        ),
        'MMM d, yyyy HH:mm',
    );
}

export function formatDateForDB(date: Date): string {
    return format(date, 'yyyy-MM-dd');
}

export function formatISODate(date: Date): string {
    return formatISO(date);
}

export function formatIsoDateWithSlashes(value: Date | string): string {
    const parsed = value instanceof Date ? value : parseISO(value);
    if (!isValid(parsed)) {
        throw new Error('Invalid date.');
    }
    return format(parsed, 'yyyy/MM/dd');
}

export function setDateToStartOfDay(date: Date): Date {
    return startOfDay(date);
}

export function getUTCDate(
    dateString?: string,
    preventConversionToLocalTimezone = false,
): Date {
    if (dateString) {
        // check if date string already includes time
        if (dateString.includes('T')) {
            return new Date(
                preventConversionToLocalTimezone
                    ? dateString.split('+')[0]
                    : dateString,
            );
        }
        // returns UTC date with time set to midnight
        return new Date(dateString + 'T00:00:00');
    }
    // Return today's date by default
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
}

export function getCurrentDateTime(desiredFormat: string = 'y_MM_dd_HH_mm_ss') {
    return format(new Date(), desiredFormat);
}

export function validateDate(dateString: string): boolean {
    const parsedMilliseconds = Date.parse(dateString);
    return !isNaN(parsedMilliseconds);
}

export function formatDateToMonth(dateString: string | Date): string {
    return formatUTCDate(dateString, 'MMM d');
}

function _relativeDateToString(dateString: Date): string {
    return formatDistance(dateString, new Date(), { addSuffix: true });
}

export function getRelativeDateString(dateString: string | Date): string {
    if (dateString instanceof Date) {
        return _relativeDateToString(dateString);
    }
    return _relativeDateToString(new Date(dateString));
}

export function getDay(dateString: string | Date): string {
    if (dateString instanceof Date) {
        return format(dateString, 'EEE');
    }
    return format(new Date(dateString), 'EEE');
}

/**
 * This function sorts a given object by its keys (which are assumed to be dates) and
 * returns a new object with the sorted keys.
 * @param data
 */
export const getSortedDateObject = (data: Record<string, any>) =>
    Object.keys(data)
        .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
        .reduce((obj, key) => {
            obj[key] = data[key];
            return obj;
        }, {} as { [key: string]: any });

/**
 * The function `getLatestDateObject` returns the latest date object from the input data,
 * which is a record of key-value pairs where the keys are date strings and the values are objects. The
 * function first sorts the data by date using the `getSortedDateObject` function, then retrieves the
 * latest date by getting the last key in the sorted data object (which is the most recent date) and
 * returns the object for that date
 * @param data
 */
export const getLatestDateObject = (data: Record<string, any>) => {
    const sortedData = getSortedDateObject(data);
    const latestDate = Object.keys(sortedData).reverse()[0];
    return sortedData[latestDate];
};

export function getFormattedDateWithLongMonth(date: string) {
    const parsedDate = parseISO(date);

    const isCurrentDate = isToday(parsedDate);

    const formattedDate = parsedDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
    return { isCurrentDate, formattedDate };
}
export const formatLastUpdatedDate = (
    lastUpdated: Date | undefined,
): string => {
    if (lastUpdated) {
        return (
            lastUpdated
                .toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                })
                .replace(/\//g, '-') || ''
        );
    }
    return '';
};

export const convertDaysToMinutes = (days: number) => {
    return days * 24 * 60;
};

export const convertHoursToMinutes = (hours: number) => {
    return hours * 60;
};

export const convertMinutesToDHM = (minutes: number) => {
    const days = Math.floor(minutes / (24 * 60));
    const remainingMinutesAfterDays = minutes - days * (24 * 60);
    const hours = Math.floor(remainingMinutesAfterDays / 60);
    const remainingMinutes = remainingMinutesAfterDays % 60;
    return { days, hours, remainingMinutes };
};

export const isWeekend = (dateString: string): boolean => {
    // parseISO reads a yyyy-MM-dd string as local midnight; new Date() would
    // read it as UTC midnight, which is the previous day west of UTC.
    const date = parseISO(dateString);
    const dayOfWeek = date.getDay();
    return [0, 6].includes(dayOfWeek);
};

export const formatDayMonth = (
    dateString: Date | string,
    dateFormat: string = 'yyyy-MM-dd',
): string => {
    if (!dateString) {
        throw new Error('Date is required.');
    }

    let parsedDate: Date;

    // Check if the input is a Date object or a string
    if (dateString instanceof Date) {
        parsedDate = dateString;
    } else {
        if (dateFormat) {
            parsedDate = parse(dateString, dateFormat, new Date()); // Parse date string with the provided format
        } else {
            throw new Error('Date format is required for date strings.');
        }

        if (!isValid(parsedDate)) {
            throw new Error('Invalid date.');
        }
    }

    const day = parsedDate.getDate().toString().padStart(2, '0');
    const month = (parsedDate.getMonth() + 1).toString().padStart(2, '0');

    return `${day}.${month}`;
};

export const getNDaysBeforeDate = (date: Date, nDays: number): string => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        throw new Error('Invalid date: The date must be a valid Date object.');
    }
    const dateNDaysAgo = subDays(date, nDays);
    return format(dateNDaysAgo, 'yyyy-MM-dd');
};

export const formatDateStringWithMidnight = (dateString: string): string => {
    // Parse the date string in the 'yyyy-MM-dd' format
    const parsedDate = parse(dateString, 'yyyy-MM-dd', new Date());

    // Validate the parsed date
    if (!isValid(parsedDate)) {
        throw new Error('Invalid date format. Expected format: yyyy-MM-dd');
    }

    // Format the date back with the midnight time
    const formattedDateStr = format(parsedDate, 'yyyy-MM-dd') + 'T00:00:00';

    return formattedDateStr;
};

export function minutesToMilliseconds(minutes: number): number {
    // Validate input range
    if (minutes < 1 || minutes > 30) {
        throw new Error('Minutes must be between 1 and 30');
    }

    // Convert minutes to milliseconds
    // 1 minute = 60 seconds
    // 1 second = 1000 milliseconds
    // So 1 minute = 60 * 1000 milliseconds
    return minutes * 60 * 1000;
}
